// @givmo/app-charity — the charity console's half of the `@givmo/step-up` seam (CCM-R1-STEP-UP; the
// seam agreed with Account Security, 2026-10-02).
//
// The package owns the gate and the prompt; this file answers for the console's Firebase session:
// which way back in this person has, the re-authentication itself, the forced token refresh, the
// second-factor code (completed by the sheet's own code step), the words a failed sign-in shows (the
// auth screens' `AuthError` mapping), and Account Security's passkey arm (AS-WEB10): the slot filled
// with the console's ACCOUNT-BOUND re-confirmation (`confirmWithPasskey`), never the unbound passkey
// sign-in. Two facts come from the server's own records: whether the account holds a passkey
// (`GET /users/me/passkeys`), and whether it holds an authenticator app (`GET /users/me/security`'s
// `mfa.totp_enrolled`) — `undefined` while either is not served, so a dark environment draws exactly
// the sheet it drew before. And the account reset (AS-WEB13, `renderLost`), wherever the security read
// is served and the account has an address: the sheet's lost links open it in place.
//
// ⚠ IT READS THOSE ONLY WHEN A PROMPT OPENS (AS-WEB15). The console may mount this host once at its
// shell, so it makes no request on render or window focus: it observes what the cache holds (the
// Sign-in & security card's own reads land there) and reads both when the gate asks (`prepare`), as a
// refusal is about to open the sheet.
//
// "Sign in again" (AS-PWL-2e, `signInAgain`): the cure for a first passkey on a sign-in older than
// rule 1's window, which only a NEW sign-in gives. The console's sign-out releases the suite session
// too, so no silent handoff brings the old sign-in back (a handoff's evidence is the origin's, law
// 8); then a full load of THIS page, signed out, whose own guard sends it to its family's sign-in
// door with this page as the `redirect`: the console's door for an officer's page, the director
// door for Board's director pages (the cut's review, F2). The full load also leaves no previous
// session's data in memory. Once the sign-out has happened, nothing unsaved on the page can be kept,
// so the console's unsaved-changes guard is told the leave is deliberate before the page goes: a
// "Leave site?" there could only ask about a sign-out already done (Compliance's O1 on the cut).

import { useMemo, type ReactNode } from 'react';
import { toast } from '@givmo/design-system';
import {
  AuthError,
  secondFactorChallengeOf,
  type AdminProviderId,
  type TotpChallenge,
} from '@givmo/auth';
import {
  PasskeySetupOffer,
  factorResetRenderLost,
  passkeyStepUpSlot,
  passkeysUsableHere,
  useStepUpHostFactors,
} from '@givmo/passkeys';
import {
  StepUpHostProvider,
  type StepUpHostAdapter,
  type StepUpOwnWayIn,
  type StepUpSetupOfferProps,
} from '@givmo/step-up';
import type { User, UserCredential } from 'firebase/auth';
import { useAuth } from '../../auth/auth-context';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { AUTH_ROUTES } from '../auth/auth-routes';
import { TotpChallengePrompt } from '../auth/totp-challenge';

/**
 * Every way back in the account holds, from its linked sign-ins, in the order a prompt tries them.
 *
 * A linked password comes before a linked provider: it always works, whereas a popup can be blocked
 * by the browser, and this prompt has no redirect fallback (a redirect would navigate away from the
 * half-filled form the retry depends on). The provider stays listed behind it, so a prompt that no
 * longer accepts the password still meets the person. The console signs admins in with Google or
 * Microsoft only, so no other provider is listed.
 */
export function stepUpWaysInFor(user: User | null): readonly StepUpOwnWayIn[] {
  if (user === null) return [];
  const providerIds = user.providerData.map((entry) => entry.providerId);
  const waysIn: StepUpOwnWayIn[] = [];
  if (providerIds.includes('password')) waysIn.push({ kind: 'password' });
  if (providerIds.includes('google.com'))
    waysIn.push({ kind: 'provider', wayIn: 'google', id: 'google', label: 'Google' });
  if (providerIds.includes('microsoft.com'))
    waysIn.push({ kind: 'provider', wayIn: 'microsoft', id: 'microsoft', label: 'Microsoft' });
  return waysIn;
}

/** The words a failed re-authentication shows, as the auth screens show them; a cancel shows none. */
function describeAuthError(thrown: unknown): string | null {
  const authError = AuthError.from(thrown);
  if (authError.kind === 'cancelled') return null;
  return authError.message.length > 0 ? authError.message : AuthError.userMessage('unknown');
}

/**
 * The sheet's own code step, completing the pending challenge `@givmo/auth` raised. The challenge
 * refuses an already-spent code without reaching Firebase; a wrong, expired or spent code is the one
 * refusal the step words as the code's (`invalidVerificationCode`).
 */
const CODE_STEP: NonNullable<StepUpHostAdapter['codeStep']> = {
  resolve: async (challenge, code) => {
    await (challenge as TotpChallenge<UserCredential>).resolve(code);
  },
  isRejectedCode: (thrown) => AuthError.from(thrown).kind === 'invalidVerificationCode',
};

/**
 * Where "Sign in again" goes once signed out: back to this page, whose own guard picks the door
 * (the console's sign-in door, or the director door for Board's director pages) and carries this
 * page as its `redirect`, never a door named here. A page the sanitizer refuses is never carried:
 * the console's door alone.
 */
export function signInAgainTarget(here: string): string {
  return sanitizeRedirectTarget(here) ?? AUTH_ROUTES.loginHome;
}

/**
 * "Sign in again": the console's sign-out FIRST (Firebase and the suite session), then `go` back to
 * `here`, signed out, for its guard to send on to its door. A failed sign-out goes nowhere and says
 * so: reloading the page still signed in would bring the old sign-in straight back. Between the two,
 * `leaveWithoutAsking` (the unsaved-changes guard's): the sign-out has already ended whatever was
 * unsaved, so the page goes without a prompt.
 */
export async function signInAgainFrom(
  here: string,
  signOut: () => Promise<void>,
  go: (url: string) => void,
  leaveWithoutAsking: () => void,
): Promise<void> {
  try {
    await signOut();
  } catch {
    toast.error('We couldn’t sign you out. Please try again.');
    return;
  }
  leaveWithoutAsking();
  go(signInAgainTarget(here));
}

/** The console's host adapter, from its live auth context. */
export function useCharityStepUpHost(): StepUpHostAdapter {
  const {
    user,
    reauthenticateWithPassword,
    reauthenticateWithProvider,
    refreshIdToken,
    confirmWithPasskey,
    signOut,
  } = useAuth();
  const { holdsPasskey, holdsAuthenticator, security, prepare, preparing } = useStepUpHostFactors(
    user?.uid ?? null,
  );
  const { leaveWithoutAsking } = useUnsavedChangesGuard();
  const passkeysUsable = passkeysUsableHere();
  return useMemo<StepUpHostAdapter>(() => {
    const waysIn = stepUpWaysInFor(user);
    // The account reset, where it can run (AS-WEB13): both lost links open it inside the sheet,
    // and the Sign-in & security card's "Can't use a passkey?" in its own dialog. "Lost your
    // authenticator?" opens D-AS-21's B2d-lost there: a recovery code, or the emailed reset
    // (AS-WEB18), whose words say passkeys go too only where the account may hold one.
    const renderLost = factorResetRenderLost({
      email: user?.email,
      security,
      waysIn,
      holdsPasskey,
      uid: user?.uid ?? null,
      // A spent recovery code or a finished reset ends every session of the account (AS-BE63):
      // the result's Done signs out here, releasing the suite session too.
      signOut,
    });
    return {
      waysIn,
      reauthenticateWithPassword: async (password) => {
        await reauthenticateWithPassword(password);
      },
      reauthenticateWithProvider: async (providerId) => {
        await reauthenticateWithProvider(providerId as AdminProviderId);
      },
      refreshIdToken,
      secondFactorOf: (thrown) => secondFactorChallengeOf<UserCredential>(thrown),
      // Kept for the seam's shape; the sheet draws its own step whenever `codeStep` is given.
      renderSecondFactor: ({
        challenge,
        title,
        description,
        cancelLabel,
        onResolved,
        onCancel,
      }) => (
        <TotpChallengePrompt
          challenge={challenge as TotpChallenge<UserCredential>}
          onResolved={onResolved}
          onCancel={onCancel}
          cancelLabel={cancelLabel}
          title={title}
          description={description}
        />
      ),
      codeStep: CODE_STEP,
      describeError: describeAuthError,
      // The passkey arm, once the account's passkey list is served here (dark: no arm at all).
      ...(holdsPasskey !== undefined
        ? { passkey: passkeyStepUpSlot({ holdsPasskey, reconfirm: confirmWithPasskey }) }
        : {}),
      ...(holdsAuthenticator !== undefined ? { holdsAuthenticator } : {}),
      ...(renderLost !== undefined ? { renderLost } : {}),
      // The account's facts are read when a prompt opens, not on render (AS-WEB15).
      prepare,
      preparing,
      // MCP-P6d — a first passkey for an org-console act only a passkey can answer, where one can
      // be made: this console's `givmo.io` host, never a raw `*.web.app` one.
      ...(passkeysUsable
        ? {
            renderPasskeySetupOffer: (offer: StepUpSetupOfferProps) => (
              <PasskeySetupOffer {...offer} uid={user?.uid ?? null} />
            ),
          }
        : {}),
      // AS-PWL-2e — a new sign-in, back on this page (the header).
      signInAgain: () => {
        void signInAgainFrom(
          `${window.location.pathname}${window.location.search}`,
          signOut,
          (url) => window.location.assign(url),
          leaveWithoutAsking,
        );
      },
    };
  }, [
    user,
    reauthenticateWithPassword,
    reauthenticateWithProvider,
    refreshIdToken,
    confirmWithPasskey,
    signOut,
    holdsPasskey,
    holdsAuthenticator,
    security,
    prepare,
    preparing,
    leaveWithoutAsking,
    passkeysUsable,
  ]);
}

/**
 * The console's host, provided once to every gate beneath it (CCM-R1-SIGN-WEB), with the ONE prompt
 * they share (CCM-R1-STEP-UP-B): a module's gate that names no host of its own (Compliance's signing
 * acts, its sign-in check card) answers through this one. Payout's gates join it too; a form rendered
 * where no provider exists hands its gate this host itself.
 */
export function CharityStepUpHostProvider({ children }: { children: ReactNode }) {
  const adapter = useCharityStepUpHost();
  return <StepUpHostProvider adapter={adapter}>{children}</StepUpHostProvider>;
}
